// pages/ManageStaffs.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  alpha,
  useTheme,
  Pagination,
  Snackbar,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  TableSortLabel,
  Fade,
  Grow,
  Zoom,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userService, User, Role, CreateUserRequest, UpdateUserRequest } from '../services/userService';

const ManageStaffs: React.FC = () => {
  const theme = useTheme();
  const { user: currentUser, hasRole } = useAuth();
  
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate state for input field
  const [roleFilter, setRoleFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form data
  const [createFormData, setCreateFormData] = useState<CreateUserRequest>({
    users_name: '',
    email: '',
    phone: '',
    designation: '',
    password: '',
    confirmPassword: '',
    role_id: 0,
    is_active: true,
    is_superuser: false,
  });
  
  const [editFormData, setEditFormData] = useState<UpdateUserRequest>({
    users_name: '',
    role_id: 0,
    designation: '',
    is_active: true,
    is_superuser: false,
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Sorting
  const [sortBy, setSortBy] = useState<'users_name' | 'email' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load roles once on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  // Fetch users when filters/pagination change
  useEffect(() => {
    fetchUsers();
  }, [page, searchTerm, roleFilter, statusFilter, sortBy, sortOrder]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page,
        limit: 10,
      };
      
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role_id = roleFilter;
      if (statusFilter) params.is_active = statusFilter === 'active';
      
      const response = await userService.getUsers(params);
      
      // Sort results
      let sortedResults = [...response.results];
      sortedResults.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        
        if (sortBy === 'users_name' || sortBy === 'email') {
          aVal = (aVal as string).toLowerCase();
          bVal = (bVal as string).toLowerCase();
        }
        
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
      
      setUsers(sortedResults);
      setTotalRecords(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / 10));
    } catch (err: any) {
      console.error('Fetch users error:', err);
      setError(err.message || 'Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesData = await userService.getRoles();
      console.log('Roles data:', rolesData);
      setRoles(rolesData);
    } catch (err: any) {
      console.error('Fetch roles error:', err);
      setRoles([]);
      setError('Failed to fetch roles. Some features may not work properly.');
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearchTerm(searchInput); // Update searchTerm which will trigger useEffect
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const handleSort = (field: 'users_name' | 'email' | 'created_at') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setCreateFormData({
      users_name: '',
      phone: '',
      email: '',
      designation: '',
      password: '',
      confirmPassword: '',
      role_id: 0,
      is_active: true,
      is_superuser: false,
    });
    setFormErrors({});
    setShowPassword(false);
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      users_name: user.users_name,
      role_id: user.role.id,
      designation: user.designation,
      is_active: user.is_active,
      is_superuser: user.is_superuser || false,
      password: '',
    });
    setFormErrors({});
    setShowPassword(false);
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (user: User) => {
    // Prevent self-deletion
    if (user.users_id === currentUser?.users_id) {
      setError('You cannot delete yourself!');
      return;
    }
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const formatDateTime = (date: string | Date) => {
    let d: Date;
    
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
      // Parse DD-MM-YYYY HH:mm:ss format
      const [datePart, timePart] = date.split(' ');
      const [day, month, year] = datePart.split('-');
      const timeString = timePart || '00:00:00';
      // Create date in YYYY-MM-DD format for proper parsing
      d = new Date(`${year}-${month}-${day}T${timeString}`);
    } else {
      d = new Date(date);
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
  };

  const validateCreateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!createFormData.users_name.trim()) {
      errors.users_name = 'Name is required';
    }
    
    if (!createFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createFormData.email)) {
      errors.email = 'Invalid email format';
    }
    
    if (createFormData.phone && !/^\d+$/.test(createFormData.phone)) {
      errors.phone = 'Invalid phone number';
    }
    
    if (!createFormData.password) {
      errors.password = 'Password is required';
    } else if (createFormData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(createFormData.password)) {
      errors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    // Confirm password validation
    if (!createFormData.confirmPassword) {
      errors.confirmPassword = 'Please re-type your password';
    } else if (createFormData.password !== createFormData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!createFormData.role_id) {
      errors.role_id = 'Role is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!editFormData.users_name?.trim()) {
      errors.users_name = 'Name is required';
    }
    
    if (!editFormData.role_id) {
      errors.role_id = 'Role is required';
    }
    
    // Only validate password if it's provided
    if (editFormData.password) {
      if (editFormData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(editFormData.password)) {
        errors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateCreateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      await userService.createUser(createFormData);
      setSuccessMessage('User created successfully');
      setCreateDialogOpen(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setCreateFormData({
        users_name: '',
        email: '',
        phone: '',
        designation: '',
        password: '',
        confirmPassword: '', // Add this
        role_id: 0,
        is_active: true,
        is_superuser: false
      });
      setFormErrors({});
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !validateEditForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare update data (exclude password if not provided)
      const updateData: UpdateUserRequest = {
        users_name: editFormData.users_name,
        role_id: editFormData.role_id,
        designation: editFormData.designation,
        is_active: editFormData.is_active,
        is_superuser: editFormData.is_superuser,
      };
      
      // Only include password if it's provided
      if (editFormData.password && editFormData.password.trim()) {
        updateData.password = editFormData.password;
      }
      
      await userService.updateUser(selectedUser.users_id, updateData);
      setSuccessMessage('User updated successfully');
      setEditDialogOpen(false);
      setShowPassword(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    // Double-check: Prevent self-deletion
    if (selectedUser.users_id === currentUser?.users_id) {
      setError('You cannot delete yourself!');
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      setLoading(true);
      await userService.deleteUser(selectedUser.users_id);
      setSuccessMessage('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    // Prevent self-deactivation
    if (user.users_id === currentUser?.users_id) {
      setError('You cannot deactivate yourself!');
      return;
    }
    
    try {
      setLoading(true);
      await userService.toggleUserStatus(user.users_id, !user.is_active);
      setSuccessMessage(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (roleId: number) => {
    if (!Array.isArray(roles)) return 'Unknown';
    const role = roles.find(r => r.id === roleId);
    return role?.role_name.replace(/_/g, ' ') || 'Unknown';
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'SYSTEM_ADMIN':
        return '#f44336';
      case 'DATA_MANAGER':
        return '#2196F3';
      case 'DATA_ENTRY_OFFICER':
        return '#4CAF50';
      case 'LEAD_MANAGER':
        return '#9C27B0';
      default:
        return '#757575';
    }
  };

  const getSerialNumber = (index: number) => {
    return (page - 1) * 10 + index + 1;
  };

  // Check if user has permission - Only DATA_MANAGER can access this page
  if (!hasRole('DATA_MANAGER')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body2">
            You don't have permission to access this page. Only DATA_MANAGER role can manage staff.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Fade in={true} timeout={600}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                Manage Staff
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage system users, roles, and permissions
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenCreateDialog}
              sx={{
                borderRadius: 2.5,
                px: 3,
                py: 1.2,
                fontWeight: 600,
                backgroundColor: '#996600',
                boxShadow: `0 8px 16px ${alpha('#996600', 0.3)}`,
                '&:hover': {
                  backgroundColor: '#664400',
                  boxShadow: `0 12px 24px ${alpha('#664400', 0.4)}`,
                },
              }}
            >
              Add New User
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grow in={true} timeout={800}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      {totalRecords}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), width: 56, height: 56 }}>
                    <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.1)} 0%, ${alpha('#4CAF50', 0.05)} 100%)`,
              border: `1px solid ${alpha('#4CAF50', 0.2)}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#4CAF50' }}>
                      {users.filter(u => u.is_active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Users
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.15), width: 56, height: 56 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha('#FF9800', 0.1)} 0%, ${alpha('#FF9800', 0.05)} 100%)`,
              border: `1px solid ${alpha('#FF9800', 0.2)}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#FF9800' }}>
                      {users.filter(u => !u.is_active).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Inactive Users
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha('#FF9800', 0.15), width: 56, height: 56 }}>
                    <CancelIcon sx={{ color: '#FF9800', fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha('#9C27B0', 0.1)} 0%, ${alpha('#9C27B0', 0.05)} 100%)`,
              border: `1px solid ${alpha('#9C27B0', 0.2)}`,
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#9C27B0' }}>
                      {users.filter(u => u.is_superuser).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Superusers
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha('#9C27B0', 0.15), width: 56, height: 56 }}>
                    <SupervisorAccountIcon sx={{ color: '#9C27B0', fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grow>

      {/* Search and Filter Section */}
      <Zoom in={true} timeout={1000}>
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: `0 4px 20px ${alpha('#000', 0.08)}` }}>
          <CardContent sx={{ p: 3, py:0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Search & Filter
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Role"
                    onChange={(e) => setRoleFilter(e.target.value as number)}
                  >
                    <MenuItem value="">All Roles</MenuItem>
                    {Array.isArray(roles) && roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.role_name.replace(/_/g, ' ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{ xs: 12, md: 2 }}>
                <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearch}
                    sx={{ borderRadius: 2 }}
                  >
                    Search
                  </Button>
                  <Tooltip title="Reset Filters">
                    <IconButton onClick={handleReset} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Zoom>

      {/* Error/Success Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Main Table */}
      <Card sx={{ borderRadius: 3, boxShadow: `0 4px 20px ${alpha('#000', 0.08)}` }}>
        {loading && <LinearProgress />}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                <TableCell sx={{ fontWeight: 700, width: 60 }}>SL</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortBy === 'users_name'}
                    direction={sortBy === 'users_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('users_name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortBy === 'email'}
                    direction={sortBy === 'email' ? sortOrder : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Superuser</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortBy === 'created_at'}
                    direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                        Try adjusting your search filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow
                    key={user.users_id}
                    sx={{
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell>{getSerialNumber(index)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: getRoleColor(user.role.role_name),
                            fontSize: '0.875rem',
                            fontWeight: 600,
                          }}
                        >
                          {user.users_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user.users_name}
                          </Typography>
                          {user.users_id === currentUser?.users_id && (
                            <Chip
                              label="You"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: alpha(theme.palette.info.main, 0.15),
                                color: 'info.main',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.phone || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role.role_name.replace(/_/g, ' ')}
                        size="small"
                        sx={{
                          bgcolor: alpha(getRoleColor(user.role.role_name), 0.15),
                          color: getRoleColor(user.role.role_name),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={user.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: user.is_active
                            ? alpha('#4CAF50', 0.15)
                            : alpha('#f44336', 0.15),
                          color: user.is_active ? '#4CAF50' : '#f44336',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.is_superuser ? (
                        <Chip
                          icon={<SupervisorAccountIcon />}
                          label="Yes"
                          size="small"
                          sx={{
                            bgcolor: alpha('#9C27B0', 0.15),
                            color: '#9C27B0',
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(user.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewUser(user)}
                            sx={{
                              color: 'info.main',
                              '&:hover': { bgcolor: alpha('#2196F3', 0.1) },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(user)}
                            sx={{
                              color: 'primary.main',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(user)}
                              disabled={user.users_id === currentUser?.users_id}
                              sx={{
                                color: user.is_active ? '#FF9800' : '#4CAF50',
                                '&:hover': {
                                  bgcolor: user.is_active
                                    ? alpha('#FF9800', 0.1)
                                    : alpha('#4CAF50', 0.1),
                                },
                              }}
                            >
                              {user.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {/*
                        <Tooltip title="Delete User">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(user)}
                              disabled={user.users_id === currentUser?.users_id}
                              sx={{
                                color: 'error.main',
                                '&:hover': { bgcolor: alpha('#f44336', 0.1) },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* View User Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              User Details
            </Typography>
          </Box>
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ py: 0 }}>
          {selectedUser && (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 3, py:1 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: getRoleColor(selectedUser.role.role_name),
                    fontSize: '2rem',
                    fontWeight: 700,
                    margin: '0 auto',
                    mb: 2,
                  }}
                >
                  {selectedUser.users_name.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {selectedUser.users_name}
                </Typography>
                <Chip
                  label={selectedUser.role.role_name.replace(/_/g, ' ')}
                  sx={{
                    bgcolor: alpha(getRoleColor(selectedUser.role.role_name), 0.15),
                    color: getRoleColor(selectedUser.role.role_name),
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Email Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedUser.phone || '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      icon={selectedUser.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      label={selectedUser.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: selectedUser.is_active
                          ? alpha('#4CAF50', 0.15)
                          : alpha('#f44336', 0.15),
                        color: selectedUser.is_active ? '#4CAF50' : '#f44336',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Superuser
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {selectedUser.is_superuser ? (
                      <Chip
                        icon={<SupervisorAccountIcon />}
                        label="Yes"
                        size="small"
                        sx={{
                          bgcolor: alpha('#9C27B0', 0.15),
                          color: '#9C27B0',
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Typography variant="body2">No</Typography>
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDateTime(selectedUser.created_at)}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedUser.created_by}
                  </Typography>
                </Grid>

                {selectedUser.last_login && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Login
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDateTime(selectedUser.last_login)}
                  </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'success.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create New User
            </Typography>
          </Box>
          <IconButton
            onClick={() => setCreateDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }} sx={{ py:2 }}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={createFormData.users_name}
                onChange={(e) => setCreateFormData({ ...createFormData, users_name: e.target.value })}
                error={!!formErrors.users_name}
                helperText={formErrors.users_name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                required
                value={createFormData.email}
                onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                placeholder="Enter Phone Number"
                value={createFormData.phone}
                onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                error={!!formErrors.phone}
                helperText={formErrors.phone || ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Designation"
                required
                value={createFormData.designation}
                onChange={(e) => setCreateFormData({ ...createFormData, designation: e.target.value })}
                error={!!formErrors.designation}
                helperText={formErrors.designation}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                required
                value={createFormData.password}
                onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                error={!!formErrors.password}
                helperText={formErrors.password || 'Min 8 chars, uppercase, lowercase, number, special char'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Re-type Password"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={createFormData.confirmPassword}
                onChange={(e) => {
                  setCreateFormData({ ...createFormData, confirmPassword: e.target.value });
                  // Clear error when user starts typing
                  if (formErrors.confirmPassword) {
                    setFormErrors({ ...formErrors, confirmPassword: '' });
                  }
                }}
                onBlur={() => {
                  // Validate on blur
                  if (createFormData.password && createFormData.confirmPassword && 
                      createFormData.password !== createFormData.confirmPassword) {
                    setFormErrors({ ...formErrors, confirmPassword: 'Passwords do not match' });
                  }
                }}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required error={!!formErrors.role_id}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={createFormData.role_id}
                  label="Role"
                  onChange={(e) => setCreateFormData({ ...createFormData, role_id: e.target.value as number })}
                >
                  <MenuItem value={0} disabled>
                    Select a role
                  </MenuItem>
                  {Array.isArray(roles) && roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.role_name.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.role_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createFormData.is_active}
                    onChange={(e) => setCreateFormData({ ...createFormData, is_active: e.target.checked })}
                    color="success"
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createFormData.is_superuser}
                    onChange={(e) => setCreateFormData({ ...createFormData, is_superuser: e.target.checked })}
                    color="secondary"
                  />
                }
                label="Superuser (Full admin access)"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={loading}
            startIcon={<PersonAddIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            {loading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit User
            </Typography>
          </Box>
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {selectedUser && (
            <>
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Restriction: Email and Phone cannot be edited
                </Typography>
                <Typography variant="caption">
                  You can update: Name, Role, Status and Superuser privilege
                </Typography>
              </Alert>

              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    required
                    value={editFormData.users_name}
                    onChange={(e) => setEditFormData({ ...editFormData, users_name: e.target.value })}
                    error={!!formErrors.users_name}
                    helperText={formErrors.users_name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={selectedUser.email}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Email cannot be changed"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={selectedUser.phone || 'Not provided'}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Phone cannot be changed"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Designation"
                    required
                    value={editFormData.designation}
                    onChange={(e) => setEditFormData({ ...editFormData, designation: e.target.value })}
                    error={!!formErrors.designation}
                    helperText={formErrors.designation}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/*
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="New Password (Optional)"
                    type={showPassword ? 'text' : 'password'}
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    error={!!formErrors.password}
                    helperText={formErrors.password || 'Leave blank to keep current password'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                */}
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth required error={!!formErrors.role_id}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={editFormData.role_id}  // Changed from selectedUser.role.id
                      label="Role"
                      onChange={(e) => setEditFormData({ ...editFormData, role_id: e.target.value as number })}
                      disabled={selectedUser.users_id === currentUser?.users_id}
                    >
                      {Array.isArray(roles) && roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.role_name.replace(/_/g, ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.role_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {formErrors.role_id}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editFormData.is_active}
                        onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                        color="success"
                        disabled={selectedUser.users_id === currentUser?.users_id}
                      />
                    }
                    label="Active"
                  />
                  {selectedUser.users_id === currentUser?.users_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                      You cannot deactivate yourself
                    </Typography>
                  )}
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editFormData.is_superuser}
                        onChange={(e) => setEditFormData({ ...editFormData, is_superuser: e.target.checked })}
                        color="secondary"
                      />
                    }
                    label="Superuser (Full admin access)"
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={loading}
            startIcon={<EditIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            {loading ? 'Updating...' : 'Update User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Delete User
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            This action cannot be undone. All data associated with this user will be permanently deleted.
          </Alert>

          {selectedUser && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                Are you sure you want to delete:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                {selectedUser.users_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser.email}
              </Typography>
              {selectedUser.users_id === currentUser?.users_id && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Warning: You cannot delete yourself!
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
            variant="outlined"
            sx={{ flex: 1, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            disabled={loading || (selectedUser?.users_id === currentUser?.users_id)}
            startIcon={<DeleteIcon />}
            sx={{ flex: 1, borderRadius: 2 }}
          >
            {loading ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageStaffs;